<script setup lang="ts">
import type { FieldUIProp } from '~/types/elementsUi'
import { useInjectedId } from '~/composables/useInjectedId'
import type { FAIconName } from '#shared/types/generated-icons'
import type { BaseTailwindColor } from '#shared/types/elementsUi'

type VFieldEmits = {
  labelIconClick: [value: Event]
  iconLeftClick: [value: Event]
  iconRightClick: [value: Event]
}

interface FieldProps {
  label?: string
  color?: BaseTailwindColor
  addons?: boolean
  labelIcon?: FAIconName
  iconLeft?: FAIconName
  iconRight?: FAIconName
  message?: string
  type?: 'div' | 'fieldset'

  ui?: FieldUIProp

  // A11y props
  name?: string
  forKey?: string
}

const emits = defineEmits<VFieldEmits>()
const props = defineProps<FieldProps>()

const { t, te } = useI18n()

const $form = useInjectedId(props.name)

const color = computed(() => props.color)
const computedLabel = computed(() => {
  if (props.label) {
    return te(props.label) ? t(props.label) : props.label
  } else {
    return undefined
  }
})

const forLabel = computed(() => {
  if (props.forKey) {
    return props.forKey
  } else {
    return $form.elementId.value
  }
})

provide($FieldColor, {
  color: color
})
</script>

<template>
  <component
    :is="type ?? 'div'"
    class="field"
    :class="[(addons || $slots['addons']) && 'has-addons', ui?.field]"
  >
    <component
      v-if="label || $slots['label']"
      :is="type === 'fieldset' ? 'legend' : 'label'"
      class="field-label"
      :class="ui?.label"
      :for="type === 'fieldset' ? undefined : forLabel"
    >
      <VIcon
        v-if="labelIcon"
        :icon="labelIcon"
        class="mr-1"
        @click="emits('labelIconClick', $event)"
      />
      <slot name="label">
        {{ computedLabel }}
      </slot>
    </component>
    <div class="field-inner">
      <div
        class="field-control"
        :class="[
          (iconLeft || $slots['icon-left']) && 'has-icons-left',
          (iconRight || $slots['icon-right']) && 'has-icons-right',
          (addons || $slots['addons']) && 'expanded',
          ui?.control
        ]"
      >
        <slot name="icon-left">
          <VIcon
            v-if="iconLeft"
            :icon="iconLeft"
            size="small"
            class="left"
            override-clickable
            @click="emits('iconLeftClick', $event)"
          />
        </slot>

        <slot />

        <slot name="icon-right">
          <VIcon
            v-if="iconRight"
            :icon="iconRight"
            size="small"
            class="right"
            override-clickable
            @click="emits('iconRightClick', $event)"
          />
        </slot>
      </div>
      <div v-if="$slots['addons']" class="field-addons">
        <slot name="addons" />
      </div>
    </div>
    <span v-if="message" class="field-message" :class="color && `text-${color}`">
      {{ message }}
    </span>
  </component>
</template>

<style scoped></style>
